(function () {
  'use strict';

  if (window.metaboxMwEditHydration) return;

  const normalizeHelpers = window.metaboxMwNormalize || {};
  const resolver = window.metaboxMwResolver || {};

  const normalizeOptionName = normalizeHelpers.normalizeOptionName || ((value) => String(value || '').trim().toLowerCase());
  const normalizeEntityName = normalizeHelpers.normalizeEntityName || normalizeOptionName;
  const decodeHtmlEntities = normalizeHelpers.decodeHtmlEntities || ((value) => String(value || '').trim());
  const findBestNormalizedMatch = normalizeHelpers.findBestNormalizedMatch || (() => null);
  const getMwValueSwatchTitle = normalizeHelpers.getMwValueSwatchTitle || (() => '');
  const getMwValueLabel = normalizeHelpers.getMwValueLabel || (() => '');
  const findMatchingSlot = resolver.findMatchingSlot || (() => undefined);

  const EDIT_HYDRATION_MAX_ATTEMPTS = 8;
  const EDIT_HYDRATION_RETRY_MS = 250;
  const HYDRATION_PHASE_COMPONENT = 0;
  const HYDRATION_PHASE_DEFAULT = 1;
  const HYDRATION_PHASE_MATERIAL = 2;

  let editSessionPropertiesPromise = null;

  function getActiveEditSession() {
    const editSession = window.mwApoEditSession;
    if (!editSession || !editSession.isEditing || !editSession.cartId) return null;
    return editSession;
  }

  function maybeHydrateEditSessionSelections(mwApoContainer, deps) {
    const editSession = getActiveEditSession();
    if (!editSession || !mwApoContainer) return;

    if (mwApoContainer.dataset.mwApoEditHydratedForCartId === editSession.cartId) return;
    if (mwApoContainer.dataset.mwApoEditHydrationInFlight === editSession.cartId) return;
    if (mwApoContainer.dataset.mwApoEditHydrationAttemptedForCartId === editSession.cartId) return;

    mwApoContainer.dataset.mwApoEditHydrationAttemptedForCartId = editSession.cartId;
    mwApoContainer.dataset.mwApoEditHydrationInFlight = editSession.cartId;
    bindEditHydrationAbort(mwApoContainer, editSession);

    hydrateEditSessionSelections(mwApoContainer, editSession, deps)
      .catch((error) => {
        console.error('[Metabox-MW] Failed to hydrate edit session selections', error);
      })
      .finally(() => {
        if (mwApoContainer.dataset.mwApoEditHydrationInFlight === editSession.cartId) {
          delete mwApoContainer.dataset.mwApoEditHydrationInFlight;
        }
      });
  }

  async function hydrateEditSessionSelections(mwApoContainer, editSession, deps) {
    const visibleProperties = await getEditSessionVisibleProperties(editSession);
    if (!visibleProperties.length) return;

    let attemptCount = 0;
    let pendingProperties = visibleProperties.slice();

    const runPass = async () => {
      if (mwApoContainer.dataset.mwApoEditHydrationCancelledForCartId === editSession.cartId) {
        return;
      }

      attemptCount += 1;

      const result = await applyVisiblePropertiesToMwOptions(mwApoContainer, pendingProperties, deps);
      const previousPendingCount = pendingProperties.length;
      pendingProperties = result.remainingProperties;

      if (!pendingProperties.length) {
        await syncSelectedMwOptionsToMetabox(mwApoContainer, deps);
        mwApoContainer.dataset.mwApoEditHydratedForCartId = editSession.cartId;
        return;
      }

      if (attemptCount >= EDIT_HYDRATION_MAX_ATTEMPTS) {
        await syncSelectedMwOptionsToMetabox(mwApoContainer, deps);
        console.warn('[Metabox-MW] Edit session hydration incomplete', {
          cartId: editSession.cartId,
          remainingProperties: pendingProperties.map((property) => property.name),
        });
        return;
      }

      if (pendingProperties.length < previousPendingCount) {
        window.setTimeout(runPass, 0);
        return;
      }

      window.setTimeout(runPass, EDIT_HYDRATION_RETRY_MS);
    };

    runPass();
  }

  async function syncSelectedMwOptionsToMetabox(mwApoContainer, deps) {
    if (!mwApoContainer) return;

    mwApoContainer.dataset.mwApoEditHydrationMute = 'true';

    try {
      const optionDescriptors = getOrderedMwOptionDescriptors(mwApoContainer, deps);

      for (const descriptor of optionDescriptors) {
        const mwOption = descriptor.mwOption;
        const checkedInput = mwOption.querySelector('input.mw-option__control:checked');
        if (checkedInput) {
          checkedInput.dispatchEvent(new Event('change', { bubbles: true }));
          await waitForHydrationStep(deps);
          continue;
        }

        const selectControl = mwOption.querySelector('select.mw-option__control');
        if (selectControl && String(selectControl.value || '').trim()) {
          selectControl.dispatchEvent(new Event('change', { bubbles: true }));
          await waitForHydrationStep(deps);
        }
      }
    } finally {
      delete mwApoContainer.dataset.mwApoEditHydrationMute;
    }
  }

  function bindEditHydrationAbort(mwApoContainer, editSession) {
    if (!mwApoContainer || !editSession?.cartId) return;
    if (mwApoContainer.dataset.mwApoEditHydrationAbortBound === 'true') return;

    const abortHydrationOnUserInput = (event) => {
      const control = event.target?.closest('.mw-option__control, select.mw-option__control');
      if (!control) return;
      if (mwApoContainer.dataset.mwApoEditHydrationMute === 'true') return;
      mwApoContainer.dataset.mwApoEditHydrationCancelledForCartId = editSession.cartId;
    };

    mwApoContainer.addEventListener('change', abortHydrationOnUserInput, true);
    mwApoContainer.dataset.mwApoEditHydrationAbortBound = 'true';
  }

  async function getEditSessionVisibleProperties(editSession) {
    if (Array.isArray(editSession.selectedProperties) && editSession.selectedProperties.length) {
      return normalizeEditSessionProperties(editSession.selectedProperties);
    }

    if (!editSessionPropertiesPromise) {
      editSessionPropertiesPromise = fetch('/cart.js', {
        credentials: 'same-origin',
        headers: {
          Accept: 'application/json',
        },
      })
        .then((response) => {
          if (!response.ok) {
            throw new Error(`Cart request failed with status ${response.status}`);
          }

          return response.json();
        })
        .then((cart) => {
          const cartItems = Array.isArray(cart?.items) ? cart.items : [];
          const matchingItem = findCartItemForEditSession(cartItems, editSession);
          if (!matchingItem) {
            console.warn('[Metabox-MW] Edit session cart item not found', {
              cartId: editSession.cartId,
              variantId: editSession.variantId || null,
            });
            return [];
          }

          const visibleProperties = Object.entries(matchingItem.properties || {})
            .filter(([name, value]) => {
              if (!name || String(name).charAt(0) === '_') return false;
              return String(value || '').trim().length > 0;
            })
            .map(([name, value]) => ({
              name,
              value,
            }));

          const normalizedVisibleProperties = normalizeEditSessionProperties(visibleProperties);

          window.mwApoEditSession = {
            ...window.mwApoEditSession,
            selectedProperties: normalizedVisibleProperties,
          };

          return normalizedVisibleProperties;
        })
        .finally(() => {
          editSessionPropertiesPromise = null;
        });
    }

    return editSessionPropertiesPromise;
  }

  function findCartItemForEditSession(cartItems, editSession) {
    if (!Array.isArray(cartItems) || !editSession?.cartId) return null;

    const cartId = String(editSession.cartId);

    return cartItems.find((item) => String(item?.properties?._mws_cart || '') === cartId) || null;
  }

  function normalizeEditSessionProperties(properties) {
    if (!Array.isArray(properties)) return [];

    return properties
      .map((property) => {
        const decodedName = decodeHtmlEntities(property?.name);
        const decodedValue = decodeHtmlEntities(property?.value);

        if (!decodedName || !decodedValue) return null;

        return {
          name: decodedName,
          value: decodedValue,
          normalizedName: normalizeOptionName(decodedName),
          normalizedValue: normalizeEntityName(decodedValue),
        };
      })
      .filter(Boolean);
  }

  async function applyVisiblePropertiesToMwOptions(mwApoContainer, visibleProperties, deps) {
    const remainingProperties = [];
    const initialOptionDescriptors = getOrderedMwOptionDescriptors(mwApoContainer, deps);
    const sortedProperties = sortVisiblePropertiesForHydration(visibleProperties, initialOptionDescriptors);

    for (const property of sortedProperties) {
      const optionDescriptors = getOrderedMwOptionDescriptors(mwApoContainer, deps);
      const matchingDescriptor = findMatchingOptionDescriptor(optionDescriptors, property);
      const matchingOption = matchingDescriptor ? matchingDescriptor.mwOption : null;

      if (!matchingOption || !(await applyPropertyValueToMwOption(matchingOption, property, deps))) {
        remainingProperties.push(property);
      }
    }

    return { remainingProperties };
  }

  async function applyPropertyValueToMwOption(mwOption, property, deps) {
    if (!mwOption || !property) return false;
    if (!isMwOptionAvailable(mwOption)) return false;

    const mwValues = Array.from(mwOption.querySelectorAll('.mw-option__value')).filter(isMwValueAvailable);
    if (mwValues.length) {
      const matchingValue =
        findBestNormalizedMatch(mwValues, property.normalizedValue, (mwValue) =>
          normalizeEntityName(getMwValueSwatchTitle(mwValue))
        ) ||
        findBestNormalizedMatch(mwValues, property.normalizedValue, (mwValue, index) =>
          normalizeEntityName(getMwValueLabel(mwValue, index))
        );

      if (!matchingValue) {
        return false;
      }

      const input = matchingValue.querySelector('input.mw-option__control');
      if (!input) return false;
      if (input.checked) {
        return true;
      }

      triggerMwOptionSelection(matchingValue, input, mwOption.closest('.mw-apo-container'));
      await waitForHydrationStep(deps);
      return true;
    }

    const selectControl = mwOption.querySelector('select.mw-option__control');
    if (selectControl) {
      const matchingOption = findBestNormalizedMatch(
        Array.from(selectControl.options || []),
        property.normalizedValue,
        (option) => normalizeEntityName(option.textContent || option.value)
      );

      if (!matchingOption) return false;
      if (selectControl.value === matchingOption.value) return true;

      selectControl.value = matchingOption.value;
      selectControl.dispatchEvent(new Event('change', { bubbles: true }));
      await waitForHydrationStep(deps);
      return true;
    }

    return false;
  }

  function triggerMwOptionSelection(mwValue, input, mwApoContainer) {
    if (!mwValue || !input) return;

    const clickableTarget =
      mwValue.querySelector('label') ||
      mwValue.querySelector('.mw-option__label') ||
      mwValue;

    if (mwApoContainer) {
      mwApoContainer.dataset.mwApoEditHydrationMute = 'true';
    }

    if (typeof input.click === 'function') {
      input.click();
    } else if (clickableTarget && typeof clickableTarget.click === 'function') {
      clickableTarget.click();
    } else {
      clickableTarget.dispatchEvent(new MouseEvent('click', { bubbles: true, cancelable: true }));
    }

    input.dispatchEvent(new Event('input', { bubbles: true }));
    input.dispatchEvent(new Event('change', { bubbles: true }));

    if (mwApoContainer) {
      window.setTimeout(() => {
        if (mwApoContainer.dataset.mwApoEditHydrationMute === 'true') {
          delete mwApoContainer.dataset.mwApoEditHydrationMute;
        }
      }, 0);
    }
  }

  function getOrderedMwOptionDescriptors(mwApoContainer, deps) {
    return Array.from(mwApoContainer.querySelectorAll('.mw-option'))
      .map((mwOption, index) => {
        const label = getMwOptionLabel(mwOption);
        return {
          mwOption,
          label,
          normalizedLabel: normalizeOptionName(label),
          phase: getHydrationPhaseForOptionLabel(label, deps),
          isAvailable: isMwOptionAvailable(mwOption),
          index,
        };
      })
      .sort((left, right) => {
        if (left.isAvailable !== right.isAvailable) {
          return left.isAvailable ? -1 : 1;
        }
        if (left.phase !== right.phase) return left.phase - right.phase;
        return left.index - right.index;
      });
  }

  function sortVisiblePropertiesForHydration(visibleProperties, optionDescriptors) {
    return visibleProperties
      .map((property, index) => {
        const matchingDescriptor = findMatchingOptionDescriptor(optionDescriptors, property);
        return {
          property,
          index,
          phase: matchingDescriptor ? matchingDescriptor.phase : HYDRATION_PHASE_DEFAULT,
          optionIndex: matchingDescriptor ? matchingDescriptor.index : Number.MAX_SAFE_INTEGER,
        };
      })
      .sort((left, right) => {
        if (left.phase !== right.phase) return left.phase - right.phase;
        if (left.optionIndex !== right.optionIndex) return left.optionIndex - right.optionIndex;
        return left.index - right.index;
      })
      .map((entry) => entry.property);
  }

  function findMatchingOptionDescriptor(optionDescriptors, property) {
    if (!Array.isArray(optionDescriptors) || !property) return null;

    return findBestNormalizedMatch(
      optionDescriptors,
      property.normalizedName,
      (descriptor) => descriptor.normalizedLabel,
      (descriptor) => descriptor.isAvailable
    );
  }

  function getMwOptionLabel(mwOption) {
    return mwOption.querySelector('.mw-text.mw-text--style-strong')?.textContent?.trim() || '';
  }

  function isMwOptionAvailable(mwOption) {
    if (!mwOption || mwOption.classList.contains('mw-hidden')) return false;
    if (mwOption.hidden) return false;
    if (mwOption.getAttribute('aria-hidden') === 'true') return false;

    return Boolean(mwOption.querySelector('.mw-option__control:not([disabled])'));
  }

  function isMwValueAvailable(mwValue) {
    if (!mwValue || mwValue.classList.contains('mw-hidden')) return false;
    if (mwValue.hidden) return false;
    if (mwValue.getAttribute('aria-hidden') === 'true') return false;

    const input = mwValue.querySelector('.mw-option__control');
    return !(input && input.disabled);
  }

  function getHydrationPhaseForOptionLabel(optionLabel, deps) {
    const normalizedOptionLabel = normalizeOptionName(optionLabel);
    if (!normalizedOptionLabel) return HYDRATION_PHASE_DEFAULT;

    const rulesForOption = typeof deps?.getRulesForOption === 'function' ? deps.getRulesForOption(optionLabel) : [];
    if (rulesForOption.some((rule) => Boolean(rule.componentNameNorm || rule.componentAction))) {
      return HYDRATION_PHASE_COMPONENT;
    }

    const latestSlots = typeof deps?.getLatestSlots === 'function' ? deps.getLatestSlots() : [];
    if (rulesForOption.some((rule) => rule.setMaterial) || findMatchingSlot(latestSlots, optionLabel)) {
      return HYDRATION_PHASE_MATERIAL;
    }

    if (/\b(style|layout|orientation|position|platform|led)\b/.test(normalizedOptionLabel)) {
      return HYDRATION_PHASE_COMPONENT;
    }

    return HYDRATION_PHASE_DEFAULT;
  }

  async function waitForHydrationStep(deps) {
    const mappedActionQueue =
      typeof deps?.getMappedActionQueue === 'function' ? deps.getMappedActionQueue() : Promise.resolve();
    await Promise.resolve(mappedActionQueue).catch(() => {});
    await waitForNextPaint();
  }

  function waitForNextPaint() {
    return new Promise((resolve) => {
      if (typeof window.requestAnimationFrame === 'function') {
        window.requestAnimationFrame(() => resolve());
        return;
      }

      window.setTimeout(resolve, 0);
    });
  }

  window.metaboxMwEditHydration = {
    maybeHydrateEditSessionSelections,
  };
})();
